// src/components/panel/BestSellersCard.js
import React, { useState, useEffect } from 'react';
import { TrophyOutlined } from '@ant-design/icons';
import { Spin, Table, Tooltip } from 'antd';
import { analyticsService } from '../../services/analyticsService';

function BestSellersCard() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('week');

    useEffect(() => {
        loadData();
    }, [period]);

    const loadData = async () => {
        try {
            setLoading(true);
            // Backend now supports period parameter (day/week/month)
            const count = 10;
            const response = await analyticsService.getBestSellers(count, null, period);
            setData(response.data || []);
            setError(null);
        } catch (err) {
            console.error('Error loading best sellers:', err);
            setError('Error al cargar bestsellers');
        } finally {
            setLoading(false);
        }
    };

    const getPeriodLabel = () => {
        switch (period) {
            case 'day':
                return 'HOY';
            case 'week':
                return 'SEMANA';
            case 'month':
                return 'MES';
            default:
                return 'SEMANA';
        }
    };

    // Calculate normalized score (0-100 scale)
    const normalizeScore = (score, maxScore) => {
        if (maxScore === 0) return 0;
        return ((score / maxScore) * 100).toFixed(1);
    };

    const maxScore = data.length > 0 ? Math.max(...data.map(item => parseFloat(item.WeightedScore || 0))) : 0;

    const columns = [
        {
            title: '#',
            key: 'rank',
            width: 50,
            render: (_, __, index) => {
                if (index === 0) return <span style={{ fontSize: '20px' }}>🥇</span>;
                if (index === 1) return <span style={{ fontSize: '20px' }}>🥈</span>;
                if (index === 2) return <span style={{ fontSize: '20px' }}>🥉</span>;
                return <strong>{index + 1}</strong>;
            }
        },
        {
            title: 'Producto',
            dataIndex: 'ProductName',
            key: 'ProductName',
            ellipsis: true,
            render: (text, record) => {
                const cleanName = text ? text.replace(/-test_de_servicio$/i, '') : text;
                return (
                    <div>
                        <div style={{ fontWeight: 500 }}>{cleanName}</div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>
                            {record.Brand} - {record.product_code}
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Cant.',
            dataIndex: 'TotalQuantitySold',
            key: 'quantity',
            width: 70,
            align: 'right',
            render: (val) => parseFloat(val || 0).toFixed(0)
        },
        {
            title: 'Ventas',
            dataIndex: 'TotalRevenue',
            key: 'revenue',
            width: 120,
            align: 'right',
            render: (val) => `$${parseFloat(val || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
        },
        {
            title: (
                <Tooltip title="Score balanceado: 70% ventas + 30% volumen">
                    Score
                </Tooltip>
            ),
            dataIndex: 'WeightedScore',
            key: 'score',
            width: 80,
            align: 'right',
            render: (val) => (
                <strong style={{ color: '#52c41a' }}>
                    {normalizeScore(parseFloat(val || 0), maxScore)}
                </strong>
            )
        },
        {
            title: 'Pedidos',
            dataIndex: 'NumberOfInvoices',
            key: 'invoices',
            width: 70,
            align: 'center',
            render: (val) => val || 0
        }
    ];

    if (loading) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <TrophyOutlined className='card_icon' style={{ fontSize: '38px', color: '#faad14' }} />
                    <h2>BESTSELLERS {getPeriodLabel()}</h2>
                </div>
                
                <div className='card-content-center'>
                    <Spin tip="Cargando bestsellers..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <TrophyOutlined className='card_icon' style={{ fontSize: '38px', color: '#faad14' }} />
                    <h2>BESTSELLERS {getPeriodLabel()}</h2>
                </div>
                
                <div className='card-content-center' style={{ color: '#ff4d4f' }}>
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className='card'>
            <div className='card-inner'>
                <TrophyOutlined className='card_icon' style={{ fontSize: '38px', color: '#faad14' }} />
                <h2>BESTSELLERS {getPeriodLabel()}</h2>
            </div>
            
            {/* Period selector buttons */}
            <div className='category-period-selector' style={{ marginTop: '8px', marginBottom: '8px' }}>
                <button 
                    className={`category-period-btn ${period === 'day' ? 'active' : ''}`}
                    onClick={() => setPeriod('day')}
                >
                    Hoy
                </button>
                <button 
                    className={`category-period-btn ${period === 'week' ? 'active' : ''}`}
                    onClick={() => setPeriod('week')}
                >
                    Semana
                </button>
                <button 
                    className={`category-period-btn ${period === 'month' ? 'active' : ''}`}
                    onClick={() => setPeriod('month')}
                >
                    Mes
                </button>
            </div>

            <div style={{ fontSize: '0.85rem', color: '#8c8c8c', marginBottom: '8px', textAlign: 'center' }}>
                💡 Score balanceado: 70% ventas + 30% volumen
            </div>
            
            <div className='dashboard-table-container'>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="product_code"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: 'No hay datos en este período' }}
                />
            </div>
        </div>
    );
}

export default BestSellersCard;
